import { useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../../api/client';
import type { IntegrationState } from './context';

export async function connectIntegration(apiClient: ApiClient, integrationId: string) {
  const result = await apiClient.post<{ authorize_url: string }>('/api/assistant/gateway/integrations/connect', { integration_id: integrationId });
  const url = new URL(result.authorize_url);
  if (url.protocol !== 'https:' || url.username || url.password || url.hash) throw new Error('Invalid authorization URL');
  window.location.assign(url.toString());
}

export default function IntegrationConnections({ state, apiClient, refresh }: {
  state?: IntegrationState; apiClient: ApiClient; refresh: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const callback = useRef(false);
  useEffect(() => {
    if (callback.current) return;
    const params = new URLSearchParams(window.location.hash.slice(1));
    const nonce = params.get('mcp_link');
    const cancelled = params.has('mcp_link_cancelled');
    if (!nonce && !cancelled) return;
    callback.current = true;
    params.delete('mcp_link'); params.delete('mcp_link_cancelled');
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${params.size ? `#${params}` : ''}`);
    if (cancelled) { setMessage('Connection cancelled. No assistant access was granted.'); return; }
    setBusy(true);
    void apiClient.post('/api/assistant/gateway/integrations/complete', { state: nonce })
      .then(async () => { setMessage('Account connected. Review the assistant’s requested tools below.'); await refresh(); })
      .catch(() => setMessage('Connection could not be confirmed. Start a new connection; the authorization code will not be retried.'))
      .finally(() => setBusy(false));
  }, [apiClient, refresh]);
  const connect = async (id: string) => {
    setBusy(true); setMessage(null);
    try { await connectIntegration(apiClient, id); }
    catch { setMessage('The app connection is unavailable. Its owner may need to check access policy or OAuth registration.'); setBusy(false); }
  };
  const disconnect = async (id: string) => {
    setBusy(true); setMessage(null);
    try {
      await apiClient.post('/api/assistant/gateway/integrations/disconnect', { integration_id: id });
      await refresh(); setMessage('Disconnected. New app calls are blocked.');
    } catch { setMessage('Disconnect was not confirmed. Refresh to check the saved connection.'); }
    finally { setBusy(false); }
  };
  return <section className="personal-gateway-card" aria-labelledby="assistant-apps-title">
    <h2 id="assistant-apps-title">Apps and tools</h2>
    <p>Connect an app account once. Your assistant requests only the tools it needs, with an expiration you can change.</p>
    {message && <p role="status">{message}</p>}
    {state?.available.map(entry => {
      const connection = state.connections.find(item => item.integration_id === entry.id);
      const connected = connection?.status === 'connected';
      return <article className="assistant-integration-connection" key={entry.id}>
        <h3>{entry.name}</h3><p>{entry.capabilities}</p>
        <p className="assistant-review-scope">{connected ? 'Account connected · assistant access needs a separate approval' : connection?.status === 'refreshing' ? 'Token refresh pending · reconnect if interrupted' : connection?.status === 'connecting' ? 'Connection not completed' : 'Account not connected'}</p>
        <div className="assistant-review-buttons"><button disabled={busy} onClick={() => { void connect(entry.id); }}>{connected ? 'Reconnect account' : 'Connect account'}</button>
          {connection && connection.status !== 'disconnected' && <button className="ghost" disabled={busy} onClick={() => { void disconnect(entry.id); }}>Disconnect</button>}</div>
      </article>;
    })}
    {!state && <p>Checking available apps…</p>}
    <p className="assistant-review-scope">Connections run through Mission Control. Executable OpenClaw plugins require operator review.</p>
  </section>;
}
